import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import qs from 'qs';
import { Status } from '../../models/common/Status';
import { RootState } from '../store';
import { Clinic } from '../../models/MedVox/Clinic';
import { apiClientWithApi, identityApiClientWithApi } from '../../utils/http';
import {
  usersParameters,
  newUserParameters,
  editUserParameters,
} from '../../hooks/components/useUsers/constants';
import { UsersCurrent } from '../../models/Users/Users';

export interface UsersState {
  items: UsersCurrent[];
  totalItemsCount: number;
  status: Status;
}

export interface UsersResponse {
  items: UsersCurrent[];
  pageIndex: number;
  totalItemsCount: number;
}

type IdentityUser = Omit<UsersCurrent, 'clinics'> & {
  readonly clinicIds: readonly string[] | null;
};

type IdentityUsersResponse = {
  readonly items: readonly IdentityUser[];
  readonly pageIndex: number;
  readonly totalItemsCount: number;
};

export interface UsersRequest {
  'Filter.ClinicIds': string[];
  'Filter.RoleIds': string[];
  'Filter.IsLockout': string;
  'Filter.TextSearch': string;
  PageIndex: number;
  PageSize: number;
  SortKeySelector: string;
  SortOrder: string;
}

export interface NewUserRequest {
  userName: string;
  password: string;
  roleId: string;
  clinicId: string[];
  isLockout: boolean;
}

export interface EditUserRequest {
  id: string;
  roleId: string;
  clinicId: string[];
  isLockout: boolean;
}

const initialState: UsersState = {
  items: [],
  totalItemsCount: 0,
  status: {
    responseLoading: true,
    responseError: null,
    requestSuccess: null,
    requestError: null,
  },
};

export const getUsers = createAsyncThunk(
  'Users/getUsers',
  async (request: UsersRequest, { rejectWithValue }) => {
    try {
      const params = {
        [usersParameters.PageIndex]: request.PageIndex,
        [usersParameters.PageSize]: request.PageSize,
        [usersParameters.SortKeySelector]: request.SortKeySelector,
        [usersParameters['Filter.ClinicIds']]: request['Filter.ClinicIds'],
        [usersParameters['Filter.RoleIds']]: request['Filter.RoleIds'],
        [usersParameters.SortOrder]: request.SortOrder,
        [usersParameters['Filter.TextSearch']]: request['Filter.TextSearch'],
      };

      if (
        request['Filter.IsLockout'] !== 'all' &&
        request['Filter.IsLockout'] !== undefined
      ) {
        params[usersParameters['Filter.IsLockout']] =
          request['Filter.IsLockout'];
      }

      const response =
        await identityApiClientWithApi.get<IdentityUsersResponse>('user', {
          params,
          paramsSerializer: function (params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
          },
        });

      const clinicIds = Array.from(
        new Set(response.data.items.flatMap((user) => user.clinicIds ?? [])),
      );
      const clinics = clinicIds.length
        ? (await apiClientWithApi.get<Clinic[]>('clinic')).data
        : [];
      const clinicsById = new Map(clinics.map((clinic) => [clinic.id, clinic]));
      const items = response.data.items.map((user) => {
        const { clinicIds: userClinicIds, ...userData } = user;
        const userClinics = Array.from(new Set(userClinicIds ?? [])).flatMap(
          (clinicId) => {
            const clinic = clinicsById.get(clinicId);
            return clinic ? [clinic] : [];
          },
        );

        return { ...userData, clinics: userClinics };
      });

      return { ...response.data, items };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      } else
        return rejectWithValue('Не удалось получить список входящих звонков!');
    }
  },
);

export const addUser = createAsyncThunk(
  'Users/addUser',
  async (request: NewUserRequest, { rejectWithValue }) => {
    try {
      const params = {
        [newUserParameters.UserName]: request.userName,
        [newUserParameters.Password]: request.password,
        [newUserParameters.RoleId]: request.roleId,
        [newUserParameters.ClinicIds]: request.clinicId,
        [newUserParameters.IsLockout]: request.isLockout,
      };

      const response = await identityApiClientWithApi.post<void>(
        'user',
        params,
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      } else return rejectWithValue('Не удалось создать пользователя!');
    }
  },
);

export const editUser = createAsyncThunk(
  'Users/editUser',
  async (request: EditUserRequest, { rejectWithValue }) => {
    try {
      const params = {
        [editUserParameters.RoleId]: request.roleId,
        [editUserParameters.ClinicIds]: request.clinicId,
        [editUserParameters.IsLockout]: request.isLockout,
      };

      await identityApiClientWithApi.patch<void>(`user/${request.id}`, params);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      } else return rejectWithValue('Не удалось создать пользователя!');
    }
  },
);

export const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(getUsers.pending, (state) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
        return state;
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.status.responseLoading = false;
        state.status.responseError = null;
        state.items = action.payload.items;
        state.totalItemsCount = action.payload.totalItemsCount;
        return state;
      })
      .addCase(getUsers.rejected, (state) => {
        state.status.responseLoading = false;
        state.status.responseError = 'Не удалось загрузить пользователей';
        return state;
      })
      .addCase(addUser.pending, (state) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
        return state;
      })
      .addCase(addUser.fulfilled, (state) => {
        state.status.responseLoading = false;
        state.status.responseError = null;
        return state;
      })
      .addCase(addUser.rejected, (state) => {
        state.status.responseLoading = false;
        state.status.responseError = 'Не удалось создать запись пользователя';
        return state;
      })
      .addCase(editUser.pending, (state) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
        return state;
      })
      .addCase(editUser.fulfilled, (state) => {
        state.status.responseLoading = false;
        state.status.responseError = null;
        return state;
      })
      .addCase(editUser.rejected, (state) => {
        state.status.responseLoading = false;
        state.status.responseError = 'Не удалось изменить запись пользователя';
        return state;
      });
  },
});

export default usersSlice.reducer;

export const selectUsers = (state: RootState) => state.users.items;

export const totalUsers = (state: RootState) => state.users.totalItemsCount;

export const usersStatus = (state: RootState) => state.users.status;
